import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '../lib/api-client';
import { usePageMeta } from '../lib/page-meta';
import { useAuth } from '../hooks/useAuth';
import AuthAside from '../components/layout/AuthAside';
import Button from '../components/ui/Button';

interface PasswordResetRequestResponse {
  accepted: true;
  resetEmail: {
    deliveryStatus: 'sent' | 'skipped' | 'failed';
    deliveryReason: string | null;
    expiresAt: string | null;
  };
}

export default function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  usePageMeta({
    title: t('forgotPassword.metaTitle'),
    description: t('forgotPassword.metaDescription'),
    canonicalPath: '/forgot-password',
    robots: 'noindex,nofollow',
  });

  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setEmailError(null);
    setFormError(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError(t('forgotPassword.errors.emailRequired'));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError(t('forgotPassword.errors.emailInvalid'));
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post<PasswordResetRequestResponse>(
        '/auth/password-reset/request',
        { email: trimmedEmail },
        { skipAuth: true },
      );
      setSubmitted(true);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormError(error.message || t('forgotPassword.errors.unexpected'));
      } else if (error instanceof TypeError) {
        setFormError(t('forgotPassword.errors.network'));
      } else {
        setFormError(t('forgotPassword.errors.unexpected'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="acc-auth-page">
      <div className="acc-auth-shell">
        <AuthAside />

        <div className="acc-auth-card acc-auth-card--panel">
          <div className="acc-auth-panel__header">
            <div className="acc-auth-panel__eyebrow">{t('forgotPassword.eyebrow')}</div>
            <h1 className="acc-auth-card__title">{t('forgotPassword.title')}</h1>
            <p className="acc-auth-card__description">{t('forgotPassword.description')}</p>
          </div>

          {formError && (
            <div role="alert" className="acc-auth-banner acc-auth-banner--danger">
              {formError}
            </div>
          )}

          {submitted ? (
            <div className="acc-auth-form" style={{ gap: 18 }}>
              <div className="acc-auth-banner acc-auth-banner--success">
                {t('forgotPassword.success')}
              </div>
              <p className="acc-auth-card__description" style={{ margin: 0 }}>
                {t('forgotPassword.successDetail')}
              </p>
              <div className="acc-auth-form__actions">
                <Button type="button" size="lg" style={{ width: '100%' }} onClick={() => navigate('/login', { replace: true })}>
                  {t('forgotPassword.backToLogin')}
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="acc-auth-form">
              <div className="acc-auth-form__grid">
                <div className="acc-auth-form__group acc-auth-form__group--span-2">
                  <label htmlFor="forgot-password-email" className="acc-auth-form__label">
                    {t('forgotPassword.emailLabel')}
                  </label>
                  <input
                    id="forgot-password-email"
                    type="email"
                    className={`acc-input${emailError ? ' acc-input--error' : ''}`}
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      setEmailError(null);
                      setFormError(null);
                    }}
                    autoComplete="email"
                    placeholder={t('forgotPassword.emailPlaceholder')}
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? 'forgot-password-email-error' : undefined}
                  />
                  {emailError && (
                    <span id="forgot-password-email-error" className="acc-auth-form__error" role="alert">
                      {emailError}
                    </span>
                  )}
                </div>

                <div className="acc-auth-form__actions acc-auth-form__actions--span-2">
                  <Button type="submit" disabled={isSubmitting} size="lg" style={{ width: '100%' }}>
                    {isSubmitting ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
                  </Button>
                </div>
              </div>
            </form>
          )}

          <p className="acc-auth-footer acc-auth-footer--panel">
            <Link to="/login" className="acc-link-inline">
              {t('forgotPassword.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
